/**
 * Pure helpers that compute the post-save `patientAddress[]` list when
 * the user commits a residential-address edit from the appointment-
 * context modal (`EditResidentialAddressView`).
 *
 * Extracted from the modal component so the four branching rules can be
 * unit-tested in isolation. Each rule mirrors a numbered block of the
 * Flutter source `edit_residential_address_profile_widget.dart:113-181`.
 *
 * The rules ARE PHI-adjacent: a wrong outcome here silently writes the
 * wrong address to the patient record. Test exhaustively.
 */

import type { PatientAddress } from '@/types/patient-api';

export type AddressSaveOutcome =
  | { ok: true; nextList: PatientAddress[] }
  | { ok: false; error: 'duplicate-location-name'; conflictName: string };

/**
 * Compute the resulting `patientAddress[]` list when the user saves
 * the modal. Steps mirror Flutter (line citations from
 * `edit_residential_address_profile_widget.dart`):
 *
 *   1. Duplicate-locationName check (`:113-125`) — case-insensitive,
 *      trimmed, ignores the edited row itself. Returns
 *      `{ok: false, error: 'duplicate-location-name', conflictName}` on
 *      collision.
 *   2. Add vs Edit branching (`:127-160`) — by matching
 *      `editedRow.contactId` against an existing entry's `contactId`.
 *      No match → append (add); match → replace at index (edit).
 *   3. isDefault enforcement (`:162-168`) — if the saved row is
 *      `isDefault === true`, clear `isDefault` on every other entry.
 *   4. At-least-one-default rule (`:170-181`) — after step 3, if no
 *      entry is default and the list is non-empty, force the first
 *      entry default.
 *
 * @param currentList The patient's existing `updatePatientContactRequest.patientAddress[]`. Pass the filtered-non-null version.
 * @param editedRow The user's edited row (Shape B, 13 fields). For an "add" save this has `contactId === null`.
 */
export function prepareAddressSave(
  currentList: PatientAddress[],
  editedRow: PatientAddress,
): AddressSaveOutcome {
  // (1) Duplicate-locationName check.
  const trimmedLn = (editedRow.locationName ?? '').trim();
  const lcLn = trimmedLn.toLowerCase();
  const collision = currentList.find(
    (a) =>
      a.contactId !== editedRow.contactId &&
      (a.locationName ?? '').trim().toLowerCase() === lcLn,
  );
  if (collision) {
    return {
      ok: false,
      error: 'duplicate-location-name',
      conflictName: trimmedLn,
    };
  }

  // (2) Add vs Edit branching.
  const editIndex = editedRow.contactId
    ? currentList.findIndex((a) => a.contactId === editedRow.contactId)
    : -1;
  let nextList: PatientAddress[];
  if (editIndex < 0) {
    nextList = [...currentList, editedRow];
  } else {
    nextList = [
      ...currentList.slice(0, editIndex),
      editedRow,
      ...currentList.slice(editIndex + 1),
    ];
  }

  // (3) isDefault enforcement.
  if (editedRow.isDefault) {
    nextList = nextList.map((a) =>
      a === editedRow ? a : { ...a, isDefault: false },
    );
  }

  // (4) At-least-one-default rule.
  const hasDefault = nextList.some((a) => a.isDefault);
  if (!hasDefault && nextList.length > 0) {
    nextList = [
      { ...nextList[0], isDefault: true },
      ...nextList.slice(1),
    ];
  }

  return { ok: true, nextList };
}
